// Subscription / tier types — mirror SubscriptionController DTOs (plan §2.4)

export type SubscriptionTier = 'BRONZE' | 'SILVER' | 'GOLD';

export type SubscriptionStatus =
    | 'TRIALING'
    | 'ACTIVE'
    | 'PAST_DUE'
    | 'CANCELED'
    | 'INCOMPLETE';

export interface UsageCounter {
    used: number;
    limit: number | null; // null = unlimited
    purchased?: number; // one-off top-up credits remaining (never resets with the period)
}

export interface CreditsCounter {
    used: number;
    limit: number;
    purchased?: number; // one-off top-up credits remaining (never resets with the period)
}

export interface SubscriptionResponse {
    // null = user has no real subscription yet (status will be INCOMPLETE).
    // Render as "no current plan" — don't highlight any tier in the comparison table.
    tier: SubscriptionTier | null;
    status: SubscriptionStatus;
    currentPeriodEnd: string | null;
    credits: CreditsCounter;
    aiAds: UsageCounter;
    pendingTier: SubscriptionTier | null;
    cancelAtPeriodEnd: boolean;
    trialEnd: string | null;
}

export interface TierResponse {
    tier: SubscriptionTier;
    displayName: string;
    priceCents: number;
    creditLimit: number;
    aiAdLimit: number | null; // null = unlimited
    stripePriceId: string;
}

// ----- One-off credit top-ups (mirrors TopUpController DTOs on BE) -----

export type CreditType = 'CAMPAIGN' | 'AI_AD';

export interface TopUpPackResponse {
    sku: string; // stable identifier, e.g. 'CAMPAIGN_PACK_10'
    creditType: CreditType;
    displayName: string;
    quantity: number; // credits granted per unit purchased
    priceCents: number;
    currency: string;
    stripePriceId: string;
}

export interface TopUpCatalogResponse {
    packs: TopUpPackResponse[];
    // Server-driven default pair so the pre-selected bundle can be retuned without an FE release.
    defaultBundle: { sku: string; quantity: number }[];
}

export interface TopUpCheckoutItem {
    sku: string;
    quantity: number;
}

export interface TopUpCheckoutResponse {
    // The purchase is a one-off Stripe Invoice (one line item per pack) so it shows up in
    // invoice history; the clientSecret is still confirmed with stripe.confirmPayment.
    clientSecret: string;
    invoiceId: string;
    amountCents: number; // server-computed total; the FE displays but never trusts its own math
    currency: string;
    lineItems: { sku: string; quantity: number; unitPriceCents: number; totalCents: number }[];
}

export interface TrialOfferResponse {
    email: string;
    tier: SubscriptionTier;
    trialDays: number;
    expiresAt: string;
}

// Structured 402 / 409 error body shapes the backend returns from
// /campaigns/pricing/activate, /subscription/change, and /ads (AI gating).
export type ActivationErrorReason =
    | 'no_active_subscription'
    | 'tier_limit'
    | 'ai_not_in_tier'
    | 'ai_limit'
    | 'over_target_quota'
    | 'no_payment_method'
    | 'upgrade_charge_failed';

export interface ActivationErrorBody {
    reason: ActivationErrorReason | string;
    cost?: number;
    remaining?: number;
    upgradeTo?: SubscriptionTier;
    details?: any;
    message?: string;
}

/**
 * Thrown by API client helpers when the backend returns a structured 402
 * (activation / quota) or 409 (downgrade-over-quota) response. Callers can
 * inspect `body.reason` to render the right upgrade modal.
 */
export class ActivationError extends Error {
    status: number;
    body: ActivationErrorBody;

    constructor(status: number, body: ActivationErrorBody, message?: string) {
        super(message || body.message || body.reason || 'Activation error');
        this.name = 'ActivationError';
        this.status = status;
        this.body = body;
    }
}

export const TIER_ORDER: SubscriptionTier[] = ['BRONZE', 'SILVER', 'GOLD'];

export function tierRank(tier: SubscriptionTier): number {
    return TIER_ORDER.indexOf(tier);
}

// ----- Payment methods + invoices (mirrors PaymentMethodDto / InvoiceDto on BE) -----

export interface PaymentMethodResponse {
    id: string;
    brand: string | null;
    last4: string | null;
    expMonth: number | null;
    expYear: number | null;
    isDefault: boolean;
}

export interface InvoiceResponse {
    id: string;
    number: string | null;
    /** What was bought, e.g. "Credit top-up — 10 Campaign Credits × 2". Null for subscription invoices. */
    description: string | null;
    amountPaid: number; // smallest currency unit
    currency: string;
    status: string; // draft | open | paid | void | uncollectible
    created: string;
    hostedInvoiceUrl: string | null;
    invoicePdf: string | null;
}

export interface SetupIntentResponse {
    clientSecret: string;
}

// Preview of a plan change — mirrors UpgradePreviewDto on the BE.
export type ChangeType = 'UPGRADE' | 'UPGRADE_TRIAL' | 'DOWNGRADE' | 'NO_CHANGE';

export interface UpgradePreviewResponse {
    changeType: ChangeType;
    immediateChargeCents: number;
    currency: string;
    prorationDate: number | null; // epoch seconds; echo back to /change
    nextRenewalDate: string | null;
    requiresPaymentMethod: boolean;
    targetTierDisplayName: string;
    targetPriceCents: number;
}
